import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableList from "../../../../components/TableList/TableList";
import { IGerAcessoVisitControlPacientLog } from "../../../../models/ger-acesso-visit-control-pacient-log";
import { useFetch } from "@4uhub/lib4uhub";
import { getAccessControlPacientSendsLog } from "../../../../services/accessControlPacientSendsLog.service";
import { IAccessControlPacientSendsLogItems } from "../../../../models/access-control-pacient-sends-log";
import {
  formatDurationSeconds,
  isSlowDuration,
  sumDurationSeconds,
} from "../../../../utils/formatDurationSeconds";

interface IGerAcessoVisitControlPacientProps {
  gerAcessoVisitControlPacientData: IGerAcessoVisitControlPacientLog;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.gerAcessoVisitControlPacient.details.";

const GerAcessoVisitControlPacientDetailsModal: React.FC<
  IGerAcessoVisitControlPacientProps
> = ({ gerAcessoVisitControlPacientData, onClose }) => {
  const { t, i18n } = useTranslation();

  const { sendRequest } = useFetch(getAccessControlPacientSendsLog);

  const [logData, setLogData] = useState<IAccessControlPacientSendsLogItems[]>(
    [],
  );

  const [selectedLogData, setSelectedLogData] = useState("");

  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const { data, success } = await sendRequest(
        gerAcessoVisitControlPacientData.identifier,
      );

      if (data && success) {
        setLogData(
          data.items.map((item, index) => ({
            ...item,
            id: index + 1,
          })),
        );
      }
    };

    fetchData();
  }, [gerAcessoVisitControlPacientData.identifier]);

  const totalSeconds = sumDurationSeconds(
    gerAcessoVisitControlPacientData.tasyToCaptureSeconds,
    gerAcessoVisitControlPacientData.captureToSentSeconds,
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const columns: GridColDef<IAccessControlPacientSendsLogItems>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "log_id") || "ID do Log",
      width: 80,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "success",
      headerName: t(translationPath + "status") || "Status",
      width: 100,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.value
                ? t(translationPath + "success") || "Sucesso"
                : t(translationPath + "error") || "Erro"
            }
            color={params.value ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "logData",
      headerName: t(translationPath + "log_data") || "Dados do Log",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const logData = params.value || "";
        const preview =
          logData.length > 150 ? logData.substring(0, 150) + "..." : logData;

        const handleShowFullLog = () => {
          setSelectedLogData(logData);
          setShowLogModal(true);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview}
            </Typography>
            {logData && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={handleShowFullLog}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes da Forubus Vaccine"} -{" "}
          {gerAcessoVisitControlPacientData.patientName}
        </DialogTitle>
        <Box sx={{ display: "flex", gap: 4, paddingX: 6, paddingTop: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t(translationPath + "tasy_released") || "Liberado no Tasy"}
            </Typography>
            <Typography variant="body2">
              {formatDate(gerAcessoVisitControlPacientData.authorizationDateReleased)}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t(translationPath + "tasy_to_capture") || "Tasy → Captura"}
              </Typography>
              <Tooltip title={t(translationPath + "tasy_to_capture_info")}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={
                isSlowDuration(gerAcessoVisitControlPacientData.tasyToCaptureSeconds)
                  ? { color: "error.main", fontWeight: 600 }
                  : undefined
              }
            >
              {formatDurationSeconds(
                gerAcessoVisitControlPacientData.tasyToCaptureSeconds,
              )}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t(translationPath + "capture_to_sent") || "Captura → Envio"}
              </Typography>
              <Tooltip title={t(translationPath + "capture_to_sent_info")}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={
                isSlowDuration(gerAcessoVisitControlPacientData.captureToSentSeconds)
                  ? { color: "error.main", fontWeight: 600 }
                  : undefined
              }
            >
              {formatDurationSeconds(
                gerAcessoVisitControlPacientData.captureToSentSeconds,
              )}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t(translationPath + "total_time") || "Tempo Total"}
              </Typography>
              <Tooltip title={t(translationPath + "total_time_info")}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Box>
            <Typography
              variant="body2"
              sx={
                isSlowDuration(totalSeconds)
                  ? { color: "error.main", fontWeight: 600 }
                  : undefined
              }
            >
              {formatDurationSeconds(totalSeconds)}
            </Typography>
          </Box>
        </Box>
        <DialogContent
          sx={{ height: 500, width: "100%", marginTop: 0, overflow: "hidden" }}
        >
          <TableList
            columns={columns}
            rows={logData}
            fileName={`nfe-logs-${gerAcessoVisitControlPacientData.patientName}`}
            showSelects={false}
            onRowSelectionModelChange={undefined}
            pageSizeOptions={[5, 10, 25, 50]}
            hideTitle={true}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={showLogModal}
        onClose={() => setShowLogModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "log_data") || "Dados do Log"}
        </DialogTitle>
        <DialogContent>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: (theme) => theme.palette.grey[100],
              padding: 2,
              borderRadius: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {selectedLogData}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogModal(false)}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GerAcessoVisitControlPacientDetailsModal;
